"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Users, AlertTriangle, Calendar, MessageSquare } from "lucide-react";

const TRIGGERS = [
  {
    icon: Calendar,
    title: { en: "Schedule published", es: "Horario publicado" },
    sub: { en: "Publish the week's schedule → every employee with a phone gets their own shifts by text.", es: "Publicas el horario de la semana → cada empleado con teléfono recibe sus turnos por SMS." },
    color: "#F4722B",
  },
  {
    icon: AlertTriangle,
    title: { en: "Time-clock alert", es: "Alerta de reloj" },
    sub: { en: "Something off in yesterday's punches? A text to the alert number you set, pointing to Payroll → Anomalies.", es: "¿Algo raro en las marcas de ayer? Un SMS al número de alertas que definas, con el camino a Nómina → Anomalías." },
    color: "#DC2626",
  },
  {
    icon: Users,
    title: { en: "Low-stock alert", es: "Alerta de stock bajo" },
    sub: { en: "An ingredient under its threshold? A text to the alert number you set, before the rush finds out.", es: "¿Un ingrediente bajo su umbral? Un SMS al número de alertas que definas, antes de que el rush se entere." },
    color: "#F4722B",
  },
];

export default function SmsStaffScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#FAFAF8] overflow-hidden">
      <div
        className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.10) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Staff SMS triggers", "Triggers SMS al staff")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Schedule screenshots? ", "¿Capturas del horario? ")}
            <span className="text-redy-charcoal/30">{t("Replaced.", "Reemplazado.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Built into Redy so staff and owner updates go out automatically — not from someone's personal WhatsApp. Weekly schedules, time-clock anomalies, low stock.",
              "Incorporado a Redy para que los avisos al staff y al dueño salgan automáticamente — no desde el WhatsApp personal de alguien. Horarios de la semana, anomalías del reloj, stock bajo."
            )}
          </p>
        </motion.div>

        {/* 3 trigger cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-[1080px] mx-auto mb-12">
          {TRIGGERS.map((trig, i) => {
            const Icon = trig.icon;
            return (
              <motion.div
                key={trig.title.en}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 0.1 * i, ease: [0.21, 0.47, 0.32, 0.98] }}
                className="bg-white border border-black/5 rounded-[20px] p-6 shadow-[0_20px_60px_rgba(0,0,0,0.04)]"
              >
                <div
                  className="w-11 h-11 rounded-[12px] flex items-center justify-center mb-5"
                  style={{ backgroundColor: `${trig.color}1f` }}
                >
                  <Icon className="w-5 h-5" style={{ color: trig.color }} />
                </div>
                <div className="text-[22px] font-black text-redy-charcoal mb-3 tracking-tight">
                  {t(trig.title.en, trig.title.es)}
                </div>
                <div className="text-[13px] font-medium text-redy-secondary leading-relaxed">
                  {t(trig.sub.en, trig.sub.es)}
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white px-4 py-2.5 rounded-full border border-black/5 shadow-sm">
            <MessageSquare className="w-3.5 h-3.5" style={{ color: "#F4722B" }} />
            <span className="text-[12px] font-bold text-redy-charcoal">
              {t("Every schedule publish is logged with how many texts went out.", "Cada publicación de horario queda registrada con cuántos SMS salieron.")}
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
